import { AddChallengeToPatientPayload } from '_api/types';
import { DaysOfWeekOptions, TargetSideOptions } from '_constants';
import {
  InputField,
  MultipleCheckboxField,
  PatientInstructionsField,
  SelectField,
} from 'components/molecules/formFields';
import React, { useEffect } from 'react';
import { usePatients, useUser } from 'utils/hooks';

import { FormBuilder } from './form-builder';
import { AddToPatientProps } from './types';

export const AddChallengeToPatientForm = ({
  initValue,
  hidePatientSelect = false,
  hideSubmit = false,
  onBack,
  onSubmit,
  onUpdate,
  contentTargetSide,
}: AddToPatientProps<AddChallengeToPatientPayload>) => {
  const { isLoading, allPatientOptions, therapistPatientsOptions } = usePatients();
  const { isAdmin } = useUser();

  useEffect(() => {
    const schedules = initValue.schedules;
    if (!schedules || schedules.length === 0) {
      update('perDay', initValue.perDay);
      update('perWeek', initValue.perWeek);
    } else {
      const active = schedules[schedules.length - 1];
      update('schedule', active.schedule);
      update('perDay', active.perDay);
      update('perWeek', active.perWeek);
    }
  }, [initValue.schedules]);

  const update = async (key: any, arg: any) => {
    if (key === 'schedule') {
      onUpdate('perWeek', arg.length);
    }

    if (key === 'perWeek') {
      switch (arg) {
        case 0:
          onUpdate('schedule', []);
          break;
        case 1:
          onUpdate('schedule', ['M']);
          break;
        case 2:
          onUpdate('schedule', ['TU', 'TH']);
          break;
        case 3:
          onUpdate('schedule', ['M', 'W', 'F']);
          break;
        case 4:
          onUpdate('schedule', ['M', 'TU', 'TH', 'F']);
          break;
        case 5:
          onUpdate('schedule', ['M', 'TU', 'W', 'TH', 'F']);
          break;
        case 6:
          onUpdate('schedule', ['M', 'TU', 'W', 'TH', 'F', 'SA']);
          break;
        default:
          onUpdate('schedule', ['M', 'TU', 'W', 'TH', 'F', 'SA', 'SU']);
      }
    }
    onUpdate(key, arg);
  };

  const patientSelect = {
    component: SelectField,
    fieldKey: 'patientId',
    label: 'Select a Patient',
    options: isAdmin ? allPatientOptions : therapistPatientsOptions,
    required: true,
    xs: 12,
  };

  const challengeFormFields = [
    {
      component: SelectField,
      fieldKey: 'defaultTargetSide',
      label: 'TARGET SIDE',
      options: TargetSideOptions || [],
      xs: 2,
      required: true,
    },
    {
      component: InputField,
      fieldKey: 'perWeek',
      label: 'TIMES PER WEEK',
      type: 'number',
      maxValue: 7,
      xs: 2,
    },
    {
      component: PatientInstructionsField,
      fieldKey: 'instructionList',
      label: 'Instructions',
      xs: 12,
      required: true,
      validation: {
        validate: (instructions: string[]) =>
          instructions?.some((instruction) => instruction.trim().length > 0) ||
          'At least one instruction is required.',
      },
    },
    {
      component: MultipleCheckboxField,
      fieldKey: 'schedule',
      options: DaysOfWeekOptions,
      xs: 12,
    },
  ];

  const initValues = {
    ...initValue,
    defaultTargetSide: contentTargetSide ?? initValue.defaultTargetSide,
  };

  return !isLoading ? (
    <FormBuilder
      initValues={initValues}
      formFields={
        hidePatientSelect ? challengeFormFields : [patientSelect, ...challengeFormFields]
      }
      onSubmit={onSubmit}
      onUpdate={update}
      onBack={onBack}
      hideSubmit={hideSubmit}
      backLabel="Cancel"
      submitLabel="Save"
    />
  ) : null;
};
